import React, { useState } from 'react';
import { Modal, Button, Tooltip } from 'reactstrap';
import { useMutation } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPaste } from '@fortawesome/free-solid-svg-icons';
import classnames from 'classnames';
import { searchTickers } from 'services/analysis';
import { parseTabularData } from 'helpers/string';

function SpreadsheetData({ data, searched }) {
  if (!data || data.length === 0 || !searched) return;

  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-min-w-full tw-border tw-border-gray-300 tw-shadow-md tw-rounded-md">
        <thead>
          <tr className="tw-bg-gray-200 tw-text-gray-700 tw-text-center">
            <th className="tw-border tw-px-4 tw-py-2">Symbol</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row, rowIndex) => {
            const ticker = row[0];
            const notFound = searched.list_not_found.includes(ticker);

            return (
              <tr
                key={rowIndex}
                className={classnames('tw-hover:bg-gray-100', {
                  'tw-text-red-500': notFound,
                })}
              >
                <td className="tw-border tw-px-4 tw-py-2 tw-text-center">
                  {ticker}
                  {notFound && <span className="tw-text-red-500">*</span>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {searched.list_not_found.length > 0 && (
        <div class="tw-flex tw-items-center tw-bg-yellow-200 tw-text-yellow-800 tw-px-4 tw-py-2 tw-rounded-md tw-shadow-sm tw-mb-4">
          <span className="tw-text-red-500">We couldn’t find these symbols with *.</span>
        </div>
      )}
    </div>
  );
}

function ExampleDataTable() {
  const rows = [
    { symbol: 'RY:TSX' },
    { symbol: 'AGLR:NEO' },
    { symbol: 'XIC:TSX' },
    { symbol: 'AAPL' },
    { symbol: 'JNK' },
    { symbol: 'FID2024' },
  ];

  return (
    <div className="tw-overflow-x-auto">
      <table className="tw-min-w-full tw-table-auto tw-border-collapse tw-border tw-border-solid tw-border-black">
        <tbody>
          {rows.map(({ symbol, value }) => (
            <tr key={symbol}>
              <td className="tw-border tw-border-solid tw-border-black tw-px-2 tw-py-1 tw-font-bold">{symbol}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function CopyAssetsModal({ isOpen, onClose, max = 10 }) {
  const [data, setData] = useState(null);
  const [searched, setSearched] = useState(null);

  const { mutateAsync: _searchTickers, isPending: isSearchingTickers } = useMutation({
    mutationFn: searchTickers,
  });

  const handlePaste = async (event) => {
    event.preventDefault();
    const pastedData = event.clipboardData.getData('text');

    const numberOfColumns = 1;
    const parsedData = parseTabularData(pastedData, numberOfColumns).slice(0, max);
    const tickers = parsedData.map((row) => _.trim(row[0]));
    const searched = await _searchTickers(tickers);

    setSearched(searched);
    setData(parsedData);
  };

  const handleSubmit = async () => {
    const validData = data.filter((row) => !searched.list_not_found.includes(row[0].trim()));
    const validMetaData = validData.map((v) => {
      const meta = searched.clean_output[v[0].trim()];
      return {
        database: meta[0],
        asset: meta[1],
      };
    });

    setData(null);
    setSearched(null);
    await onClose(validMetaData);
  };

  return (
    <Modal isOpen={isOpen} size="lg" className="tw-mt-2 tw-pt-0">
      <div className="modal-header">
        <h5 className="modal-title" id="myModalLabel">
          Copy and Paste Assets
        </h5>
      </div>
      <div className="modal-body">
        <p className="mb-1">Paste your tickers here</p>
        <div
          contentEditable
          onPaste={handlePaste}
          onBeforeInput={(e) => e.preventDefault()}
          onDrop={(e) => e.preventDefault()}
          onCut={(e) => e.preventDefault()}
          className="tw-grid tw-grid-cols-2 tw-gap-4 tw-text-gray-800 tw-min-h-[150px] tw-p-2 tw-border tw-border-[#ccc] tw-border-solid tw-select-none"
        >
          <div className="">
            <p className="mt-1">
              Please paste tickers in the first column. For Canadian listed equities and exchange-traded funds, please
              add :TSX or :NEO to the ticker according to which exchange the asset is listed on.
            </p>
            <div className="tw-text-center">
              <FontAwesomeIcon icon={faPaste} size="3x" className="" />
            </div>
          </div>
          <div className="">
            <ExampleDataTable />
          </div>
        </div>
        <SpreadsheetData data={data} searched={searched} />
      </div>
      <div className="modal-footer tw-justify-between tw-px-10">
        <div></div>
        <div>
          <Button className="btn-round" color="info" data-dismiss="modal" type="button" onClick={() => onClose()}>
            Cancel
          </Button>
          {searched && (
            <Button className="btn-round" color="success" data-dismiss="modal" type="button" onClick={handleSubmit}>
              Submit
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}

export default function CopyAssetsButton({ onClose, max = 10 }) {
  const [showModal, setShowModal] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const toggle = () => setTooltipOpen(!tooltipOpen);

  return (
    <>
      <Button
        id="copy-asset"
        className="btn"
        color="info"
        onClick={() => {
          setShowModal(true);
        }}
      >
        Copy Assets
      </Button>
      <Tooltip placement="bottom" isOpen={tooltipOpen} target="copy-asset" toggle={toggle}>
        Copy and paste your tickers
      </Tooltip>
      {showModal && (
        <CopyAssetsModal
          isOpen
          max={max}
          onClose={(data) => {
            onClose(data);
            setShowModal(false);
          }}
        />
      )}
    </>
  );
}
